import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { UsersThree, EnvelopeSimple, Copy, Trash, ShieldCheck, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { CONTACT_URL } from "@/lib/brand";

export default function Team() {
  const [state, setState] = useState({ team: null, members: [], pending_invites: [], seat_usage: { used: 0, limit: 1, remaining: 1 }, role: null });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/team");
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isOwner = !state.team || state.role === "owner";
  const canInvite = isOwner && (state.seat_usage?.remaining ?? 0) > 0;

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post("/team/invites", { email: inviteEmail.trim() });
      toast.success(`Invite sent to ${data.email}`);
      setInviteEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const cancel = async (mid) => {
    try {
      await api.delete(`/team/invites/${mid}`);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  const remove = async (uid) => {
    if (!confirm("Remove this member from the team?")) return;
    try {
      await api.delete(`/team/members/${uid}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Invite link copied"));
  };

  const su = state.seat_usage || { used: 0, limit: 1, remaining: 1 };
  const usagePct = su.limit ? Math.min(100, Math.round((su.used / su.limit) * 100)) : 0;

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors closeButton />
      <div className="max-w-[1120px] mx-auto px-6 lg:px-10 py-10">
        <Link to="/dashboard" data-testid="team-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 hover:text-ink-900 mb-6">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Team</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-ink-900">
              {state.team?.name || "Solo workspace"}
            </h1>
            <p className="mt-3 text-ink-700 max-w-2xl">
              {state.team
                ? "Members share a single workspace — every analysis, worker, and report is visible to everyone on the team."
                : "You're on a solo plan. Invite a teammate below to create a shared workspace."}
            </p>
          </div>
          <div className="border border-ink-100 p-4 min-w-[220px]" data-testid="team-seat-usage">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Seats</div>
            <div className="mt-1 flex items-baseline gap-2">
              <div className="font-display font-black text-4xl tracking-tighter text-ink-900">{su.used}</div>
              <div className="font-mono text-xs text-ink-500">/ {su.limit}</div>
            </div>
            <div className="mt-3 h-1.5 bg-ink-100">
              <div className="h-1.5 bg-safety" style={{ width: `${usagePct}%` }} />
            </div>
            <div className="mt-2 text-xs text-ink-500">{su.remaining} remaining</div>
          </div>
        </div>

        {/* Invite */}
        {isOwner && (
          <div className="mt-10 border border-ink-100 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-3">Invite a teammate</div>
            <div className="flex gap-3 flex-wrap">
              <Input
                type="email"
                data-testid="invite-email-input"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") invite(); }}
                placeholder="teammate@company.com"
                className="flex-1 min-w-[240px] rounded-none border-ink-100"
              />
              <Button
                data-testid="invite-send-btn"
                onClick={invite}
                disabled={inviting || !canInvite || !inviteEmail.trim()}
                className="rounded-none bg-ink-900 hover:bg-ink-800 text-white"
              >
                <EnvelopeSimple size={16} weight="bold" className="mr-2" />
                {inviting ? "Sending…" : "Send invite"}
              </Button>
            </div>
            {!canInvite && isOwner && su.remaining === 0 && (
              <div className="mt-4 border border-hazard-warning bg-hazard-warning/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 text-sm text-ink-900" data-testid="seat-limit-banner">
                  <div className="font-semibold">Seat limit reached ({su.limit} seats)</div>
                  <div className="text-xs text-ink-700 mt-0.5">Contact us to add seats so you can invite the next teammate.</div>
                </div>
                <a
                  href={CONTACT_URL}
                  data-testid="contact-add-seats-btn"
                  className="inline-flex items-center gap-2 bg-safety hover:bg-safety-hover text-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] whitespace-nowrap"
                >
                  Contact us to add seats <ArrowRight size={12} weight="bold" />
                </a>
              </div>
            )}
          </div>
        )}

        {/* Members */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <UsersThree size={16} weight="bold" className="text-ink-700" />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Members</div>
          </div>
          <div className="border border-ink-100 divide-y divide-ink-100" data-testid="team-members-list">
            {loading && <div className="p-4 text-sm text-ink-500">Loading…</div>}
            {!loading && state.members.length === 0 && (
              <div className="p-6 text-sm text-ink-500">No active members yet.</div>
            )}
            {state.members.map((m) => (
              <div key={m.membership_id} className="p-4 flex items-center gap-4" data-testid={`team-member-${m.user_id}`}>
                {m.picture ? (
                  <img src={m.picture} alt="" className="w-10 h-10 object-cover" />
                ) : (
                  <div className="w-10 h-10 bg-ink-100 flex items-center justify-center font-display font-black text-ink-500">
                    {(m.name || m.email).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-ink-900 truncate">{m.name || m.email}</div>
                  <div className="text-xs text-ink-500 truncate">{m.email}</div>
                </div>
                <div className={`px-2 py-1 font-mono text-[10px] uppercase tracking-[0.25em] ${m.role === "owner" ? "bg-safety text-white" : "border border-ink-200 text-ink-700"}`}>
                  {m.role === "owner" && <ShieldCheck size={10} weight="bold" className="inline mr-1" />}
                  {m.role}
                </div>
                {isOwner && m.role !== "owner" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(m.user_id)}
                    data-testid={`team-member-remove-${m.user_id}`}
                    className="rounded-none text-hazard-critical hover:bg-hazard-critical/10"
                  >
                    <Trash size={14} weight="bold" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Pending invites */}
        {isOwner && state.pending_invites.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <EnvelopeSimple size={16} weight="bold" className="text-ink-700" />
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Pending invites</div>
            </div>
            <div className="border border-ink-100 divide-y divide-ink-100" data-testid="team-pending-list">
              {state.pending_invites.map((p) => (
                <div key={p.membership_id} className="p-4 flex items-center gap-4" data-testid={`team-pending-${p.membership_id}`}>
                  <EnvelopeSimple size={20} weight="regular" className="text-ink-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink-900 truncate">{p.email}</div>
                    <div className="text-xs text-ink-500">
                      Invited {new Date(p.created_at).toLocaleDateString()} · awaiting acceptance
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyLink(`${window.location.origin}/accept-invite/${p.invite_token}`)}
                    data-testid={`team-pending-copy-${p.membership_id}`}
                    className="rounded-none"
                  >
                    <Copy size={14} weight="bold" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => cancel(p.membership_id)}
                    data-testid={`team-pending-revoke-${p.membership_id}`}
                    className="rounded-none text-hazard-critical hover:bg-hazard-critical/10"
                  >
                    <Trash size={14} weight="bold" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
